/** Multi-org membership API (Phase 24 — super-admin cross-lab access).
 *
 * react-query hooks + wire types for the super-admin "all organizations" browse/join surface
 * and the active-lab switcher. A platform super-admin can JOIN any lab (creating a `UserOrg`
 * membership) and then SWITCH their active lab; every authed request then carries
 * `X-Active-Org` (injected in `client.ts`) which the backend validates against `UserOrg` on
 * every call. Switching invalidates the ENTIRE react-query cache so all downstream
 * format/config/generation/cross-table data re-fetches scoped to the new active lab.
 *
 * Hooks mirror `accounts.ts`. Gating: `enabled` is threaded through the GET hooks so
 * `OrgProvider` can disable the `/me/active-org` + `/me/memberships` fetches for the 99%
 * non-super-admin case. `staleTime` is GLOBAL (App.tsx QueryClient: 30_000) — never set per-hook.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// ── Wire types (mirror the Phase 23/24 backend schemas) ─────────────────────────────────────────────

/** A location within an org (sub-row in the AllOrgsPage expand). */
export interface OrgLocation {
  id: string;
  name: string;
  code: string | null;
  is_default: boolean;
}

/** GET /admin/orgs row — every org plus its locations and the caller's membership flag. */
export interface OrgWithLocations {
  id: string;
  name: string;
  location_count: number;
  locations: OrgLocation[];
  /** True when the calling super-admin already has a `UserOrg` row for this org. */
  is_member: boolean;
}

/** GET /me/active-org — the lab the caller is currently acting in. */
export interface ActiveOrg {
  id: string;
  name: string;
  role: string;
}

/** GET /me/memberships — one row per lab the caller has joined.
 *  `role` is the caller's PER-LAB role in that membership (UserOrg.role), narrowed to the
 *  app's two roles so the Phase 25 switcher badge + active-membership `isAdmin` can branch on it. */
export interface OrgMembership {
  org_id: string;
  org_name: string;
  role: "admin" | "tech";
}

// ── Query keys ──────────────────────────────────────────────────────────────────────────────────────

export const ADMIN_ORGS_KEY = ["/admin/orgs"] as const;
export const ACTIVE_ORG_KEY = ["/me/active-org"] as const;
export const MY_MEMBERSHIPS_KEY = ["/me/memberships"] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────────────────────────────

/** Every org on the platform + its locations + the caller's membership flag (super-admin only). */
export function useAllOrgs(enabled = true) {
  return useQuery({
    queryKey: ADMIN_ORGS_KEY,
    queryFn: async (): Promise<OrgWithLocations[]> => {
      const { data } = await api.get<OrgWithLocations[]>("/admin/orgs");
      return data;
    },
    enabled,
  });
}

/** The caller's current active lab (the org `X-Active-Org` resolves to). */
export function useActiveOrg(enabled = true) {
  return useQuery({
    queryKey: ACTIVE_ORG_KEY,
    queryFn: async (): Promise<ActiveOrg> => {
      const { data } = await api.get<ActiveOrg>("/me/active-org");
      return data;
    },
    enabled,
  });
}

/** The labs the caller has joined (one membership per `UserOrg` row). */
export function useMyMemberships(enabled = true) {
  return useQuery({
    queryKey: MY_MEMBERSHIPS_KEY,
    queryFn: async (): Promise<OrgMembership[]> => {
      const { data } = await api.get<OrgMembership[]>("/me/memberships");
      return data;
    },
    enabled,
  });
}

/** Join a lab (creates a `UserOrg` membership). Argument is the target org id. */
export function useJoinOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string): Promise<void> => {
      await api.post(`/admin/orgs/${encodeURIComponent(orgId)}/members`, {});
    },
    onSuccess: () => {
      // The joined org's row flips to "Joined" and the new membership appears in the switcher.
      void qc.invalidateQueries({ queryKey: ADMIN_ORGS_KEY });
      void qc.invalidateQueries({ queryKey: MY_MEMBERSHIPS_KEY });
    },
  });
}

/** Switch the caller's active lab. Argument is the target org id (must already be joined).
 *  onSuccess invalidates the ENTIRE cache — every resource is org-scoped, so all of it changes. */
export function useSwitchOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string): Promise<void> => {
      await api.post("/admin/active-org", { org_id: orgId });
    },
    onSuccess: () => {
      // No filter: switching the active lab re-scopes formats, configs, generation, cross-tables… all of it.
      void qc.invalidateQueries();
    },
  });
}
